// src/app/global-error.tsx

"use client"

import NextError from "next/error"
import { useEffect } from "react"

import GoogleAnalytics from "~/components/GoogleAnalytics"
import { buildConsentBootstrap } from "~/lib/analytics/consent-mode"
import { GA_MEASUREMENT_ID, isGaDebug, shouldLoadGoogleAnalytics } from "~/lib/analytics/ga-config"
import { handleClientError } from "~/lib/client-error-handling"


/**
 * Props for the GlobalError component.
 * @interface GlobalErrorProps
 * @property {Error & { digest?: string }} error - The error to be captured.
 */
interface GlobalErrorProps {
  error: Error & { digest?: string }
}

export default function GlobalError({ error }: GlobalErrorProps): JSX.Element {
  const deploymentId =
    typeof document !== 'undefined'
      ? document.documentElement.dataset.dplId
      : undefined

  useEffect(() => {
    // This is the root-layout fatal-crash boundary — the single most important
    // error to capture. It reaches BOTH sinks through the one pipeline:
    //  - the BetterStack drain (structured `client_error` with stack + digest) so
    //    it's queryable/alertable alongside every other error; `digest` joins it
    //    to the server-side `onRequestError` log for the same crash.
    //  - GA4 via the centralized mirror inside `logClientError`, which fires
    //    `trackException` once the drain confirms the ingest — `fatal: true`,
    //    `component` and `digest` below carry through to the beacon. The gtag
    //    stub from the Consent Mode bootstrap rendered in <head> below keeps
    //    that beacon working even though the normal layout (and its bootstrap)
    //    never ran for a root-layout crash.
    handleClientError(error, {
      type: "global_error",
      component: "global-error",
      fatal: true,
      digest: error.digest,
    })
  }, [error])

  return (
    <html
      lang="en"
      translate="no"
      className="notranslate"
      data-dpl-id={deploymentId}
    >
      <head>
        {/* Re-render the Consent Mode bootstrap here: global-error replaces the
            root layout, so the layout's <head> bootstrap (the gtag stub + consent
            defaults) never ran. This keeps the fatal-error beacon consent-correct. */}
        {shouldLoadGoogleAnalytics() && (
          <script
            dangerouslySetInnerHTML={{
              __html: buildConsentBootstrap(GA_MEASUREMENT_ID, { debug: isGaDebug() }),
            }}
          />
        )}
      </head>
      <body>
        {/* Load gtag.js so the queued exception is actually delivered. */}
        <GoogleAnalytics />
        {/* `NextError` is the default Next.js error page component. Its type
        definition requires a `statusCode` prop. However, since the App Router
        does not expose status codes for errors, we simply pass 0 to render a
        generic error message. */}
        <NextError statusCode={0} />
      </body>
    </html>
  )
}
